import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { extractErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, Receipt, AlertTriangle, Loader2, Calendar,
  ArrowUpRight, ArrowDownRight, FileX, ChevronRight, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const eur = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

const eurP = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
}).format(n || 0);

const monthLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  const names = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin',
                  'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
};

export default function Comptabilite() {
  const navigate = useNavigate();
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  useEffect(() => {
    document.title = 'Comptabilité — Reno Resolve';
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/comptabilite/overview?year=${year}`);
        setData(r.data);
      } catch (e) {
        toast.error(extractErrorMessage(e, 'Erreur chargement comptabilité'));
      } finally { setLoading(false); }
    })();
    (async () => {
      setReconLoading(true);
      try {
        const from = `${year}-01-01`;
        const to = `${year}-12-31`;
        const r = await api.get(
          `/pennylane/reconcile-purchase-invoices?date_from=${from}&date_to=${to}`
        );
        setRecon(r.data);
      } catch (e) {
        setRecon({ available: false, error: 'Pennylane injoignable' });
      } finally { setReconLoading(false); }
    })();
  }, [year]);

  const availableYears = useMemo(() => {
    const list = [];
    for (let y = nowYear + 1; y >= nowYear - 4; y--) list.push(y);
    return list;
  }, [nowYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }
  if (!data) return null;

  const ca = data.ca || {};
  const tva = data.tva || {};
  const nonJust = data.non_justifiees || [];
  const solde = tva.solde || 0;

  // Ordered monthly rows for TVA table
  const months = Array.from({ length: 12 }, (_, i) => {
    const key = `${year}-${String(i + 1).padStart(2, '0')}`;
    return {
      key, label: monthLabel(key),
      ca: ca.by_month?.[key] || 0,
      ca_ttc: ca.ttc_by_month?.[key] || 0,
      collectee: tva.collectee_by_month?.[key] || 0,
      deductible: tva.deductible_by_month?.[key] || 0,
    };
  });
  const quarters = ['T1', 'T2', 'T3', 'T4'].map((q, i) => {
    const key = `${year}-${q}`;
    return {
      key, label: `Trimestre ${i + 1}`,
      ca: ca.by_quarter?.[key] || 0,
      ca_ttc: ca.ttc_by_quarter?.[key] || 0,
      collectee: tva.collectee_by_quarter?.[key] || 0,
      deductible: tva.deductible_by_quarter?.[key] || 0,
    };
  });

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Comptabilité
            </h1>
            <p className="text-xs text-slate-500">Vue admin · CA, TVA, factures non justifiées</p>
          </div>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
          <SelectTrigger className="w-40" data-testid="compta-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="CA HT annuel"
          value={eur(Object.values(ca.by_month || {}).reduce((a, b) => a + b, 0))}
          sublabel={`TTC ${eur(Object.values(ca.ttc_by_month || {}).reduce((a, b) => a + b, 0))} · ${ca.won_count || 0} devis gagné(s)`}
          icon={<TrendingUp className="w-5 h-5" />}
          accent="blue"
          testid="kpi-ca-ht"
        />
        <KpiCard
          label="TVA collectée"
          value={eur(Object.values(tva.collectee_by_month || {}).reduce((a, b) => a + b, 0))}
          sublabel="Factures de vente émises"
          icon={<ArrowUpRight className="w-5 h-5" />}
          accent="emerald"
          testid="kpi-tva-collectee"
        />
        <KpiCard
          label="TVA déductible"
          value={eur(Object.values(tva.deductible_by_month || {}).reduce((a, b) => a + b, 0))}
          sublabel="Factures d'achat"
          icon={<ArrowDownRight className="w-5 h-5" />}
          accent="slate"
          testid="kpi-tva-deductible"
        />
        <KpiCard
          label={solde >= 0 ? 'TVA nette à payer' : 'Crédit de TVA'}
          value={eur(Math.abs(solde))}
          sublabel={`Solde ${year}`}
          icon={<Receipt className="w-5 h-5" />}
          accent={solde >= 0 ? 'amber' : 'emerald'}
          testid="kpi-tva-solde"
        />
      </div>

      {/* Ventilation mensuelle */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Ventilation mensuelle {year}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">CA HT · CA TTC · TVA collectée · TVA déductible</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compta-monthly-table">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-5 py-2.5 font-semibold text-slate-600 text-xs uppercase">Mois</th>
                <th className="text-right px-5 py-2.5 font-semibold text-slate-600 text-xs uppercase">CA HT</th>
                <th className="text-right px-5 py-2.5 font-semibold text-blue-700 text-xs uppercase">CA TTC</th>
                <th className="text-right px-5 py-2.5 font-semibold text-emerald-700 text-xs uppercase">TVA collectée</th>
                <th className="text-right px-5 py-2.5 font-semibold text-slate-500 text-xs uppercase">TVA déductible</th>
                <th className="text-right px-5 py-2.5 font-semibold text-slate-900 text-xs uppercase">Solde TVA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {months.map((m) => {
                const bal = m.collectee - m.deductible;
                return (
                  <tr key={m.key} className="hover:bg-slate-50">
                    <td className="px-5 py-2 text-slate-700">{m.label}</td>
                    <td className="px-5 py-2 text-right text-slate-900 font-medium">{eur(m.ca)}</td>
                    <td className="px-5 py-2 text-right text-blue-700 font-medium">{eur(m.ca_ttc)}</td>
                    <td className="px-5 py-2 text-right text-emerald-700">{eur(m.collectee)}</td>
                    <td className="px-5 py-2 text-right text-slate-500">{eur(m.deductible)}</td>
                    <td className={`px-5 py-2 text-right font-semibold ${bal >= 0 ? 'text-slate-900' : 'text-emerald-700'}`}>
                      {eur(bal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Ventilation trimestrielle */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-900">Ventilation trimestrielle {year}</h2>
          <p className="text-xs text-slate-500 mt-0.5">Utile pour les déclarations trimestrielles</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-slate-100">
          {quarters.map((q) => (
            <div key={q.key} className="p-5" data-testid={`compta-quarter-${q.key}`}>
              <div className="text-xs text-slate-500 font-semibold uppercase mb-2">{q.label}</div>
              <div className="text-2xl font-bold text-slate-900 mb-0.5">{eur(q.ca)}</div>
              <div className="text-xs text-blue-700 font-medium mb-2">TTC {eur(q.ca_ttc)}</div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-emerald-700">TVA collectée</span>
                  <span className="font-semibold text-emerald-700">{eur(q.collectee)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">TVA déductible</span>
                  <span className="font-semibold text-slate-600">{eur(q.deductible)}</span>
                </div>
                <div className="pt-1 mt-1 border-t border-slate-100 flex justify-between">
                  <span className="text-slate-700 font-semibold">Solde</span>
                  <span className={`font-bold ${(q.collectee - q.deductible) >= 0 ? 'text-slate-900' : 'text-emerald-700'}`}>
                    {eur(q.collectee - q.deductible)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Rapprochement bancaire Pennylane */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="compta-pennylane-recon">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-blue-600" />
              Rapprochement bancaire Pennylane {year}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {reconLoading
                ? 'Récupération des relevés bancaires…'
                : recon?.available
                  ? `${recon.counts?.justified || 0} facture(s) justifiée(s) · ${recon.counts?.not_justified || 0} non justifiée(s) · ${recon.counts?.total_transactions || 0} transaction(s) sur la période`
                  : (recon?.error || 'Pennylane non configuré ou injoignable')}
            </p>
          </div>
          {reconLoading && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
        </div>

        {recon?.available && (
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
            {/* Justifiées */}
            <div>
              <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Factures justifiées</span>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                  {recon.justified?.length || 0}
                </span>
              </div>
              {(!recon.justified || recon.justified.length === 0) ? (
                <div className="p-6 text-center text-xs text-slate-500">
                  Aucune facture rapprochée avec le relevé bancaire.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                  {recon.justified.slice(0, 100).map((f) => (
                    <div key={f.facture_achat_id}
                         data-testid={`recon-justified-${f.facture_achat_id}`}
                         className="px-5 py-2.5 hover:bg-emerald-50 cursor-pointer text-xs"
                         onClick={() => navigate('/factures-achat')}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 truncate">
                            {f.numero_facture || '—'} · {typeof f.fournisseur === 'object' && f.fournisseur !== null
                              ? f.fournisseur.nom : (f.fournisseur || '—')}
                          </div>
                          <div className="text-slate-500 truncate">
                            {f.date_facture || '—'}
                            {f.bank_tx && ` · virement du ${f.bank_tx.date}`}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-bold text-slate-900">{eurP(f.total_ttc)}</div>
                          <div className="text-[10px] text-emerald-700">✓ rapproché</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Non justifiées */}
            <div>
              <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">Factures non justifiées</span>
                <span className="ml-auto px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                  {recon.not_justified?.length || 0}
                </span>
              </div>
              {(!recon.not_justified || recon.not_justified.length === 0) ? (
                <div className="p-6 text-center text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <FileX className="w-4 h-4" /> Toutes les factures sont rapprochées.
                  </span>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                  {recon.not_justified.slice(0, 100).map((f) => (
                    <div key={f.facture_achat_id}
                         data-testid={`recon-not-justified-${f.facture_achat_id}`}
                         className="px-5 py-2.5 hover:bg-amber-50 cursor-pointer text-xs"
                         onClick={() => navigate('/factures-achat')}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-900 truncate">
                            {f.numero_facture || '—'} · {typeof f.fournisseur === 'object' && f.fournisseur !== null
                              ? f.fournisseur.nom : (f.fournisseur || '—')}
                          </div>
                          <div className="text-slate-500 truncate">{f.date_facture || '—'}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-bold text-slate-900">{eurP(f.total_ttc)}</div>
                          <div className="text-[10px] text-amber-700">⚠ à rapprocher</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!reconLoading && !recon?.available && (
          <div className="p-6 text-center text-sm text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
            Configurez le token Pennylane dans <button onClick={() => navigate('/parametres')} className="text-blue-700 font-semibold hover:underline">Paramètres</button> pour rapprocher automatiquement vos factures d'achat avec vos relevés bancaires.
          </div>
        )}
      </section>

      {/* Factures non justifiées (fallback local, hors Pennylane) */}
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Factures d'achat non justifiées
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                {data.non_justifiees_count || 0}
              </span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Aucun PDF/scan attaché ni push Pennylane. À justifier pour la TVA déductible.
            </p>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => navigate('/factures-achat')}
            data-testid="compta-open-achats-btn"
          >
            Voir toutes <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
        {nonJust.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <FileX className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            Toutes les factures d'achat sont justifiées. Bravo.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compta-non-justifiees-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase">Numéro</th>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase">Fournisseur</th>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase">Date</th>
                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase">Montant TTC</th>
                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-600 uppercase">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {nonJust.slice(0, 25).map((f) => (
                  <tr key={f.facture_achat_id}
                       onClick={() => navigate('/factures-achat')}
                       data-testid={`compta-non-just-${f.facture_achat_id}`}
                       className="hover:bg-amber-50 cursor-pointer">
                    <td className="px-5 py-2 text-slate-700 font-medium">{f.numero_facture || '—'}</td>
                    <td className="px-5 py-2 text-slate-700">
                      {typeof f.fournisseur === 'object' && f.fournisseur !== null
                        ? (f.fournisseur.nom || '—')
                        : (f.fournisseur || '—')}
                    </td>
                    <td className="px-5 py-2 text-slate-500">{f.date_facture || '—'}</td>
                    <td className="px-5 py-2 text-right text-slate-900 font-semibold">{eurP(f.total_ttc)}</td>
                    <td className="px-5 py-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                        {f.statut || 'À justifier'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {nonJust.length > 25 && (
              <div className="px-5 py-3 text-center text-xs text-slate-500 bg-slate-50">
                {nonJust.length - 25} autre(s) facture(s)…
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function KpiCard({ label, value, sublabel, icon, accent, testid }) {
  const accents = {
    blue: 'bg-blue-50 text-blue-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    slate: 'bg-slate-100 text-slate-700',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accents[accent] || accents.blue}`}>
          {icon}
        </div>
      </div>
      <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}
